import { QueryClient } from "@tanstack/react-query";

/**
 * Default query client configuration for React Query.
 * 
 * Features:
 * - 5 minute stale time (data considered fresh for 5 minutes)
 * - Automatic refetch on window focus
 * - Retry failed requests 3 times with exponential backoff
 * - Don't retry on 4xx errors (client errors)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors (client errors)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for 5xx errors and network errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => {
        // Exponential backoff: 1s, 2s, 4s
        return Math.min(1000 * 2 ** attemptIndex, 30000);
      },
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
});

